import { compact } from 'lodash';
import { useMemo } from 'react';

import {
  HouseholdDetailsContext,
  useHouseholdDetailsContext,
} from '@/modules/household/contexts/householdDetails.context';
import { AfterDeath } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

/**
 * This input type is designed to mimic the output of a waterfall query
 */
export interface useWaterfallGrantorDeathDisplayInput {
  firstGrantorDeathYear?: number | null | undefined;
  secondGrantorDeathYear?: number | null | undefined;
  firstGrantorDeath?:
    | {
        id: string;
      }
    | null
    | undefined;
}

export function useWaterfallGrantorDeathDisplay(
  input: useWaterfallGrantorDeathDisplayInput | null | undefined
): Record<AfterDeath, string> {
  const {
    firstGrantorDeathYear,
    secondGrantorDeathYear,
    firstGrantorDeath: firstGrantorDeathProfile,
  } = input || {};
  const { primaryClients, isTwoClientHousehold } = useHouseholdDetailsContext();

  const firstDyingGrantorId = firstGrantorDeathProfile?.id;

  return useMemo(() => {
    return {
      [AfterDeath.None]: getDisplayForWaterfallGrantorDeath({
        afterDeath: AfterDeath.None,
        primaryClients,
        isTwoClientHousehold,
        firstGrantorDeathYear,
        secondGrantorDeathYear,
        firstDyingGrantorId,
      }),
      [AfterDeath.First]: getDisplayForWaterfallGrantorDeath({
        afterDeath: AfterDeath.First,
        primaryClients,
        isTwoClientHousehold,
        firstGrantorDeathYear,
        secondGrantorDeathYear,
        firstDyingGrantorId,
      }),
      [AfterDeath.Second]: getDisplayForWaterfallGrantorDeath({
        afterDeath: AfterDeath.Second,
        primaryClients,
        isTwoClientHousehold,
        firstGrantorDeathYear,
        secondGrantorDeathYear,
        firstDyingGrantorId,
      }),
    };
  }, [
    firstDyingGrantorId,
    firstGrantorDeathYear,
    isTwoClientHousehold,
    primaryClients,
    secondGrantorDeathYear,
  ]);
}

export interface getDisplayForWaterfallGrantorDeathInput {
  afterDeath: AfterDeath;
  primaryClients:
    | (
        | {
            id: string;
            displayGivenName: string;
          }
        | undefined
      )[]
    | null;
  isTwoClientHousehold: HouseholdDetailsContext['isTwoClientHousehold'];
  firstDyingGrantorId: string | undefined | null;
  firstGrantorDeathYear: number | undefined | null;
  secondGrantorDeathYear: number | undefined | null;
}

export function getDisplayNamesForDeaths({
  isTwoClientHousehold,
  primaryClients,
  firstDyingGrantorId,
}: Pick<
  getDisplayForWaterfallGrantorDeathInput,
  'isTwoClientHousehold' | 'primaryClients' | 'firstDyingGrantorId'
>): {
  firstNameDisplay: string;
  secondNameDisplay: string | undefined;
} {
  const firstDyingGrantorName = primaryClients?.find(
    (client) => firstDyingGrantorId === client?.id
  )?.displayGivenName;
  const lastDyingGrantorName = primaryClients?.find(
    (client) => firstDyingGrantorId !== client?.id
  )?.displayGivenName;

  let firstNameDisplay = '';

  if (firstDyingGrantorName) {
    firstNameDisplay = `${firstDyingGrantorName}'s`;
  } else if (isTwoClientHousehold) {
    firstNameDisplay = '1st Client';
  }

  let secondNameDisplay = '';
  if (lastDyingGrantorName) {
    secondNameDisplay = `${lastDyingGrantorName}'s`;
  } else if (isTwoClientHousehold) {
    secondNameDisplay = '2nd Client';
  }
  return {
    firstNameDisplay,
    secondNameDisplay,
  };
}

export interface WaterfallGrantorDeathLabelParts {
  /** Death year, shown above the title. Empty when the year is unknown. */
  year: string;
  title: string;
}

/**
 * The same death label as getDisplayForWaterfallGrantorDeath, split into a
 * year and a title so a caller can stack the two lines.
 */
export function getWaterfallGrantorDeathLabelParts({
  afterDeath,
  primaryClients,
  isTwoClientHousehold,
  firstGrantorDeathYear,
  secondGrantorDeathYear,
  firstDyingGrantorId,
}: getDisplayForWaterfallGrantorDeathInput): WaterfallGrantorDeathLabelParts {
  const { firstNameDisplay, secondNameDisplay } = getDisplayNamesForDeaths({
    isTwoClientHousehold,
    primaryClients,
    firstDyingGrantorId,
  });

  // compact drops the name when no grantor name is available, so the title
  // reads "Before death" instead of carrying a double space.
  const toTitle = (prefix: string, nameDisplay: string | undefined) =>
    compact([prefix, nameDisplay, 'death']).join(' ');

  switch (afterDeath) {
    case AfterDeath.None:
      return {
        year: firstGrantorDeathYear?.toString() ?? '',
        title: toTitle('Before', firstNameDisplay),
      };
    case AfterDeath.First:
      return {
        year: firstGrantorDeathYear?.toString() ?? '',
        title: toTitle('After', firstNameDisplay),
      };
    case AfterDeath.Second:
      return {
        year: secondGrantorDeathYear?.toString() ?? '',
        title: toTitle('After', secondNameDisplay),
      };
    default:
      throw new UnreachableError({
        case: afterDeath as never,
        message: 'Invalid AfterDeath scenario',
      });
  }
}

export function getDisplayForWaterfallGrantorDeath({
  afterDeath,
  primaryClients,
  isTwoClientHousehold,
  firstGrantorDeathYear,
  secondGrantorDeathYear,
  firstDyingGrantorId,
}: getDisplayForWaterfallGrantorDeathInput): string {
  const { firstNameDisplay, secondNameDisplay } = getDisplayNamesForDeaths({
    isTwoClientHousehold,
    primaryClients,
    firstDyingGrantorId,
  });

  const firstDeathYearDisplay = firstGrantorDeathYear
    ? ` (${firstGrantorDeathYear})`
    : '';

  const secondDeathYearDisplay = secondGrantorDeathYear
    ? ` (${secondGrantorDeathYear})`
    : '';

  switch (afterDeath) {
    case AfterDeath.None:
      return `Before ${firstNameDisplay} death${firstDeathYearDisplay}`;
    case AfterDeath.First:
      return `After ${firstNameDisplay} death${firstDeathYearDisplay}`;
    case AfterDeath.Second:
      return `After ${secondNameDisplay} death${secondDeathYearDisplay}`;
    default:
      throw new UnreachableError({
        case: afterDeath as never,
        message: 'Invalid AfterDeath scenario',
      });
  }
}
